import { useCallback, useEffect, useRef, useState } from 'react';
import { AuthDialog, type AuthMode } from './auth/AuthDialog';
import { getCurrentUser, logout, type AuthenticatedUser } from './auth/authApi';
import { CreatorDashboard } from './editor/CreatorDashboard';
import { PhaserGame } from './game/PhaserGame';
import { PublishedGamePage } from './publication/PublishedGamePage';
import {
  legalPageByPath,
  LegalPage,
  SiteFooter,
  type LegalPageKey,
} from './legal/LegalPages';
import type { TemplateKey } from '../shared/templates';
import { clientTemplateRegistry } from './templates/templateRegistry';

type PlatformView = 'home' | 'templates' | 'dashboard' | 'new-game' | 'demo' | LegalPageKey;

const sampleLeaders = [
  { name: 'Deniz K.', score: 420, duration: '02:18' },
  { name: 'Ece Y.', score: 390, duration: '02:41' },
  { name: 'Mert A.', score: 350, duration: '03:02' },
];

const sampleImages = [
  { src: '/odevlendir/assets/home/train-level-1.webp', template: 'Tren Macerası', accent: 'train', alt: 'Birinci seviyede tek soru vagonu ve hareket eden cevap balonları', description: 'Hareket eden doğru balonu zamanında vagona indir.' },
  { src: '/odevlendir/assets/home/train-level-2-first-question.webp', template: 'Tren Macerası', accent: 'train', alt: 'İkinci seviyede ilk soru vagonuna ilerleyen cevap balonları', description: 'Her seviyede soruları doğru sırayla tamamla.' },
  { src: '/odevlendir/assets/home/train-level-2-second-question.webp', template: 'Tren Macerası', accent: 'train', alt: 'İkinci seviyede tamamlanan ilk vagon ve aktif ikinci soru vagonu', description: 'Tren ilerlerken sıradaki aktif vagona odaklan.' },
  { src: '/odevlendir/assets/home/train-level-3.webp', template: 'Tren Macerası', accent: 'train', alt: 'Üçüncü seviyede tamamlanan ve sıradaki soru vagonları', description: 'Tamamlanan vagonları geride bırakıp yeni sorulara ulaş.' },
  { src: '/odevlendir/assets/home/fiery-route-overview.webp', template: 'Alevli Rota', accent: 'route', alt: 'Alevli Rota haritasında üç cevap alanı ve Kıvıl karakteri', description: 'Kıvıl ile renkli rotalarda doğru cevap odasını bul.' },
  { src: '/odevlendir/assets/home/fiery-route-chase.webp', template: 'Alevli Rota', accent: 'route', alt: 'Kıvıl karakterinin Çiselerden kaçtığı yakın oyun görünümü', description: 'Çiselerden kaç, canlarını koru ve doğru geçide ulaş.' },
  { src: '/odevlendir/assets/home/orbital-bridge-start.webp', template: 'Yörünge Köprüsü', accent: 'orbital', alt: 'Yörünge Köprüsü oyununda soru, enerji modülleri ve keşif aracı', description: 'Doğru enerji modülünü seçerek eksik köprüyü kur.' },
  { src: '/odevlendir/assets/home/orbital-bridge-crossing.webp', template: 'Yörünge Köprüsü', accent: 'orbital', alt: 'Kurulan köprüden geçen paletli keşif aracı', description: 'Köprü tamamlanınca keşif aracının yolculuğunu izle.' },
  { src: '/odevlendir/assets/home/orbital-bridge-question.webp', template: 'Yörünge Köprüsü', accent: 'orbital', alt: 'Uzay ortamında yeni köprü boşluğuna ulaşan keşif aracı', description: 'Kesintisiz uzay yolculuğunda yeni sorulara ilerle.' },
  { src: '/odevlendir/assets/home/turbo-duel-start.webp', template: 'Turbo Düellosu', accent: 'turbo', alt: 'Turbo Düellosu yarışının başlangıcında üç araç ve cevap kartları', description: 'İki bilgisayar kontrollü rakibe karşı ikinci sırada başla.' },
  { src: '/odevlendir/assets/home/turbo-duel-race.webp', template: 'Turbo Düellosu', accent: 'turbo', alt: 'Dağ yolunda yarışan üç araç ve cevap seçenekleri', description: 'Doğru ve hızlı cevaplarla rakiplerini yakala.' },
  { src: '/odevlendir/assets/home/turbo-duel-boost.webp', template: 'Turbo Düellosu', accent: 'turbo', alt: 'Turbo etkisiyle yarışta öne geçen araçlar', description: 'Turboyu ateşle, sollama yap ve bitişe önde ulaş.' },
];

const homeBenefits = [
  { icon: '👤', title: 'Hesapsız oynama', description: 'Öğrenciler hesap oluşturmadan yalnızca ad ve soyadlarıyla oyuna katılır.' },
  { icon: '🔗', title: 'Süreli ve özel bağlantı', description: 'Etkinliği belirlediğin süre boyunca yalnızca bağlantıyı paylaştığın öğrenciler açar.' },
  { icon: '📊', title: 'Ayrıntılı öğrenci raporu', description: 'Puan, süre, doğru-yanlış ve soru bazlı sonuçları öğretmen panelinden incele.' },
];

const howItWorksSteps = [
  { title: 'Şablonunu seç', description: 'Hazırlamak istediğin öğrenme oyununu belirle.' },
  { title: 'Sorularını hazırla', description: 'Metin ve görsellerle kendi içeriğini oluştur.' },
  { title: 'Bağlantını gönder', description: 'Yayın süresini seçip etkinliği öğrencilerinle paylaş.' },
  { title: 'Sonuçları incele', description: 'Öğrenci gelişimini ve soru bazlı sonuçları panelinden takip et.' },
];

const frequentlyAskedQuestions = [
  { question: 'Öğrencilerin hesap oluşturması gerekiyor mu?', answer: 'Hayır. Öğrenciler bağlantıyı açıp ad ve soyadlarını girdikten sonra doğrudan oynayabilir.' },
  { question: 'Etkinlik bağlantısı ne kadar süre açık kalır?', answer: 'Yayınlarken 1 saat, 4 saat, 1 gün, 2 gün veya 7 gün seçeneklerinden birini belirleyebilirsin.' },
  { question: 'Öğrencilerin hangi sonuçlarını görebilirim?', answer: 'Tamamlanma durumu, puan, toplam süre, doğru-yanlış sayıları ve destekleyen oyunlarda soru bazlı ayrıntılar gösterilir.' },
  { question: 'Sorulara ve cevaplara görsel ekleyebilir miyim?', answer: 'Evet. Şablonun desteklediği alanlarda yalnız metin, yalnız görsel veya metin ve görseli birlikte kullanabilirsin.' },
  { question: 'Oyunlar telefon ve akıllı tahtada çalışır mı?', answer: 'Evet. Oyun, editör ve öğretmen paneli mobil tarayıcıları, masaüstünü ve akıllı tahtaları destekleyecek şekilde uyarlanır.' },
  { question: 'Yayınladığım oyunu erken kapatabilir miyim?', answer: 'Evet. Öğretmen panelindeki “Yayından kaldır” seçeneğiyle bağlantıyı süresi dolmadan kapatabilirsin.' },
];

export function App() {
  const match = window.location.pathname.match(/^\/odevlendir\/oyna\/([^/]+)\/?$/);
  return match ? <PublishedGamePage slug={decodeURIComponent(match[1])} /> : <PlatformApp />;
}

function PlatformApp() {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<AuthMode>('login');
  const [view, setView] = useState<PlatformView>(() => {
    const normalizedPath = window.location.pathname.replace(/\/$/, '') || '/';
    return legalPageByPath[normalizedPath] ?? 'home';
  });
  const [pendingView, setPendingView] = useState<PlatformView | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateKey>('train-adventure');

  useEffect(() => {
    let active = true;
    getCurrentUser()
      .then((currentUser) => {
        if (active) setUser(currentUser);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setAuthLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setPendingView(null);
  }, []);
  const openDialog = (mode: AuthMode, afterAuthentication?: PlatformView) => {
    setDialogMode(mode);
    setPendingView(afterAuthentication ?? null);
    setDialogOpen(true);
  };

  const openProtectedView = (nextView: 'dashboard' | 'new-game') => {
    if (!user) {
      openDialog('register', nextView);
      return;
    }
    setView(nextView);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const signOut = async () => {
    await logout();
    setUser(null);
    setView('home');
  };

  const goHome = () => {
    window.history.pushState({}, '', '/odevlendir/');
    setView('home');
  };

  return (
    <div className="site-shell">
      <header className="topbar">
        <a className="brand" href="/odevlendir/" onClick={(event) => {
          event.preventDefault();
          goHome();
        }} aria-label="Oyunla Ödevlendir ana sayfa">
          <span className="brand-mark" aria-hidden="true">
            <img src="/odevlendir/assets/brand/tekk-logo.jpg" alt="" />
          </span>
          <span>
            <strong>Oyunla Ödevlendir</strong>
            <small>Kendi eğitsel oyununu oluştur</small>
          </span>
        </a>
        <nav aria-label="Ana menü">
          {user ? (
            <>
              <span className="account-email" title={user.email}>{user.email}</span>
              <button className="ghost-button" type="button" onClick={() => void signOut()}>Çıkış yap</button>
            </>
          ) : (
            <button className="ghost-button" type="button" onClick={() => openDialog('login', 'dashboard')} disabled={authLoading}>
              Giriş yap
            </button>
          )}
          <button className="primary-button" type="button" onClick={() => user ? openProtectedView('dashboard') : setView('templates')}>
            {user ? 'Panelim' : 'Oyun oluştur'}
          </button>
        </nav>
      </header>

      <main>
        {view === 'home' && (
          <HomePage onCreate={() => setView('templates')} onDemo={() => setView('demo')} />
        )}
        {view === 'templates' && (
          <TemplateGallery
            onBack={() => setView('home')}
            onSelect={(templateKey) => {
              setSelectedTemplate(templateKey);
              openProtectedView('new-game');
            }}
          />
        )}
        {view === 'dashboard' && user && <CreatorDashboard isAdmin={user.role === 'admin'} />}
        {view === 'new-game' && user && (
          <CreatorDashboard key={`new-game-${selectedTemplate}`} initialCreate initialTemplateKey={selectedTemplate} isAdmin={user.role === 'admin'} />
        )}
        {view === 'demo' && <DemoGamePage onBack={() => setView('home')} />}
        {(view === 'kvkk' || view === 'cookies' || view === 'privacy' || view === 'terms') && (
          <LegalPage page={view} />
        )}
      </main>
      <AuthDialog
        open={dialogOpen}
        initialMode={dialogMode}
        onClose={closeDialog}
        onAuthenticated={(authenticatedUser) => {
          setUser(authenticatedUser);
          setView(pendingView ?? 'dashboard');
        }}
      />
      <SiteFooter />
    </div>
  );
}

function HomePage({ onCreate, onDemo }: { onCreate: () => void; onDemo: () => void }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [carouselPaused, setCarouselPaused] = useState(false);
  const [carouselInteracting, setCarouselInteracting] = useState(false);
  const [pageHidden, setPageHidden] = useState(document.hidden);
  const touchStartX = useRef<number | null>(null);
  const activeSample = sampleImages[currentSlide];

  const showPreviousSlide = useCallback(() => {
    setCurrentSlide((index) => (index - 1 + sampleImages.length) % sampleImages.length);
  }, []);
  const showNextSlide = useCallback(() => {
    setCurrentSlide((index) => (index + 1) % sampleImages.length);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => setPageHidden(document.hidden);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (carouselPaused || carouselInteracting || pageHidden) return undefined;
    const timer = window.setInterval(showNextSlide, 5_000);
    return () => window.clearInterval(timer);
  }, [carouselInteracting, carouselPaused, currentSlide, pageHidden, showNextSlide]);

  return (
    <>
      <section className="home-hero" aria-labelledby="home-title">
        <div>
          <p className="eyebrow">Öğrenmeyi oyuna dönüştür</p>
          <h1 id="home-title">Eğlenerek öğrensinler, sen gelişimlerini takip et.</h1>
          <p>Hazır oyun şablonlarını kendi sorularınla düzenle; öğrencilerine doğrudan oynat veya süreli bir bağlantıyla ödev ver.</p>
        </div>
        <div className="hero-journey" aria-label="Oyunla ödevlendirme adımları">
          <span>Sorularını Hazırla</span><b aria-hidden="true">→</b>
          <span>Oyunu Yayınla</span><b aria-hidden="true">→</b>
          <span>Öğrencilerine Gönder</span><b aria-hidden="true">→</b>
          <span>Ayrıntılı Sonuçlarını Gör</span>
        </div>
      </section>

      <section className="create-callout" aria-labelledby="create-callout-title">
        <div>
          <p className="eyebrow">Kendi içeriğinle</p>
          <h2 id="create-callout-title">İlk eğitsel oyununu oluştur</h2>
          <p>Şablonunu seç, sorularını ve görsellerini ekle, bağlantını öğrencilerinle paylaş.</p>
        </div>
        <button className="create-game-button" type="button" onClick={onCreate}>Oyun oluştur <span aria-hidden="true">→</span></button>
      </section>

      <section className="benefit-section" aria-labelledby="benefit-title">
        <div className="section-heading">
          <p className="eyebrow">Öğretmen için kolay, öğrenci için eğlenceli</p>
          <h2 id="benefit-title">Ödevlendirmeyi birkaç adımda tamamla</h2>
        </div>
        <div className="benefit-grid">
          {homeBenefits.map((benefit) => (
            <article className="benefit-card" key={benefit.title}>
              <span className="benefit-icon" aria-hidden="true">{benefit.icon}</span>
              <h3>{benefit.title}</h3>
              <p>{benefit.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="sample-gallery" aria-labelledby="sample-gallery-title">
        <div className="section-heading">
          <p className="eyebrow">{clientTemplateRegistry.length} özgün oyun şablonu</p>
          <h2 id="sample-gallery-title">Oyunlardan örnek görüntüler</h2>
          <p>Tren yolculuğundan yarış pistine uzanan farklı oyun dünyalarını incele.</p>
        </div>
        <div
          className="sample-carousel"
          role="region"
          aria-roledescription="slayt gösterisi"
          aria-label="Oyun şablonlarından örnek görüntüler"
          tabIndex={0}
          onMouseEnter={() => setCarouselInteracting(true)}
          onMouseLeave={() => setCarouselInteracting(false)}
          onFocusCapture={() => setCarouselInteracting(true)}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setCarouselInteracting(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              showPreviousSlide();
            } else if (event.key === 'ArrowRight') {
              event.preventDefault();
              showNextSlide();
            }
          }}
          onTouchStart={(event) => { touchStartX.current = event.changedTouches[0]?.clientX ?? null; }}
          onTouchEnd={(event) => {
            if (touchStartX.current === null) return;
            const distance = (event.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
            touchStartX.current = null;
            if (Math.abs(distance) < 45) return;
            if (distance > 0) showPreviousSlide(); else showNextSlide();
          }}
        >
          <div className={`sample-slide sample-slide-${activeSample.accent}`}>
            <img key={activeSample.src} src={activeSample.src} alt={activeSample.alt} loading={currentSlide === 0 ? 'eager' : 'lazy'} />
            <div className="sample-caption">
              <div>
                <p className="eyebrow">{activeSample.template}</p>
                <p>{activeSample.description}</p>
              </div>
              <strong aria-label={`${sampleImages.length} görüntüden ${currentSlide + 1}. görüntü`}>{currentSlide + 1} / {sampleImages.length}</strong>
            </div>
          </div>
          <button className="carousel-arrow previous" type="button" onClick={showPreviousSlide} aria-label="Önceki görüntü">‹</button>
          <button className="carousel-arrow next" type="button" onClick={showNextSlide} aria-label="Sonraki görüntü">›</button>
          <button className="carousel-pause" type="button" onClick={() => setCarouselPaused((paused) => !paused)} aria-label={carouselPaused ? 'Slayt gösterisini devam ettir' : 'Slayt gösterisini duraklat'}>
            <span aria-hidden="true">{carouselPaused ? '▶' : 'Ⅱ'}</span>
            {carouselPaused ? 'Devam et' : 'Duraklat'}
          </button>
          <div className="carousel-dots" aria-label="Görüntü seçimi">
            {sampleImages.map((image, index) => (
              <button
                className={`carousel-dot carousel-dot-${image.accent}${index === currentSlide ? ' active' : ''}`}
                type="button"
                key={image.src}
                onClick={() => setCurrentSlide(index)}
                aria-label={`${index + 1}. görüntü: ${image.template}`}
                aria-current={index === currentSlide ? 'true' : undefined}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="demo-callout" aria-labelledby="demo-callout-title">
        <div>
          <p className="eyebrow">Önce deneyebilirsin</p>
          <h2 id="demo-callout-title">Tren Macerası nasıl oynanıyor?</h2>
          <p>Örnek sorularla beş seviyelik oyunu hesap oluşturmadan aç.</p>
        </div>
        <button className="ghost-button" type="button" onClick={onDemo}>Örnek oyunu oyna</button>
      </section>

      <section className="how-it-works" aria-labelledby="how-it-works-title">
        <div className="section-heading">
          <p className="eyebrow">Adım adım</p>
          <h2 id="how-it-works-title">Nasıl çalışır?</h2>
          <p>İçeriğini hazırla, bağlantını paylaş ve öğrencilerinin gelişimini tek panelden takip et.</p>
        </div>
        <ol className="how-it-works-list">
          {howItWorksSteps.map((step, index) => (
            <li key={step.title}>
              <span aria-hidden="true">{index + 1}</span>
              <div><h3>{step.title}</h3><p>{step.description}</p></div>
            </li>
          ))}
        </ol>
      </section>

      <section className="faq-section" aria-labelledby="faq-title">
        <div className="section-heading">
          <p className="eyebrow">Merak edilenler</p>
          <h2 id="faq-title">Sık sorulan sorular</h2>
        </div>
        <div className="faq-list">
          {frequentlyAskedQuestions.map((item) => (
            <details key={item.question}>
              <summary>{item.question}<span aria-hidden="true">+</span></summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>
    </>
  );
}

function TemplateGallery({ onBack, onSelect }: { onBack: () => void; onSelect: (templateKey: TemplateKey) => void }) {
  return (
    <section className="template-page" aria-labelledby="template-title">
      <button className="ghost-button" type="button" onClick={onBack}>← Ana sayfa</button>
      <div className="section-heading">
        <p className="eyebrow">Oyun şablonları</p>
        <h1 id="template-title">Nasıl bir öğrenme yolculuğu hazırlayacaksın?</h1>
        <p>Şablonu seçtikten sonra soru, cevap, görsel ve seviye ayarlarını düzenleyebilirsin.</p>
      </div>
      <div className="template-grid">
        {clientTemplateRegistry.map((template) => (
          <article className={`template-card${template.key === 'fiery-route' ? ' fiery-route-template-card' : ''}${template.key === 'orbital-bridge' ? ' orbital-template-card' : ''}${template.key === 'turbo-duel' ? ' turbo-template-card' : ''}${template.key === 'balance-arena' ? ' balance-template-card' : ''}${template.key === 'basket-learning' ? ' basket-template-card' : ''}${template.key === 'goal-arena' ? ' goal-template-card' : ''}${template.key === 'billionaire-quiz' ? ' billionaire-template-card' : ''}`} key={template.key}>
            {template.visual === 'train-image' ? (
              <img src="/odevlendir/assets/train-adventure/preview-level-2.png" alt="Tren Macerası şablonunun oyun görünümü" />
            ) : template.visual === 'mosaic-art' ? (
              <div className="fiery-route-card-art" aria-label="Büyülü Mozaik Bahçesi'nde Kıvıl ve Çiseler">
                <span className="route-card-fire">K</span><span className="route-card-path" /><span className="route-card-water">Ç</span>
              </div>
            ) : template.visual === 'station-art' ? (
              <div className="station-card-art" aria-label="Üç raylı istasyonda soru tabelası, tren ve koşan kaşif">
                <span className="station-card-sky" /><span className="station-card-rails" /><span className="station-card-train" />
                <span className="station-card-sign">İSTASYON 3</span><span className="station-card-runner">🏃</span><span className="station-card-caption">HER İSTASYONDA BİR SORU</span>
              </div>
            ) : template.visual === 'rally-art' ? (
              <div className="rally-card-art" aria-label="Neon otoyolda A, B, C cevap tabelalarına doğru giden mavi elektrikli yarış arabası">
                <span className="rally-card-sun" /><span className="rally-card-road" />
                <span className="rally-card-gate"><b>A</b><b>B</b><b>C</b></span>
                <span className="rally-card-car" /><span className="rally-card-caption">DOĞRU ŞERİDE GEÇ</span>
              </div>
            ) : template.visual === 'ufo-art' ? (
              <div className="ufo-card-art" aria-label="Mavi gezegenin önünde ışıklı geçitlere uçan metal UFO">
                <span className="ufo-card-planet"/><span className="ufo-card-portal"/><img src="/odevlendir/assets/ufo-adventure/ufo.svg" alt=""/><span className="ufo-card-caption">YENİ DÜNYALAR SENİ BEKLİYOR</span>
              </div>
            ) : template.visual === 'orbital-art' ? (
              <div className="orbital-card-art" aria-label="Yıldızların arasında eksik bir köprü ve paletli keşif aracı">
                <span className="orbital-planet" /><span className="orbital-platform left" /><span className="orbital-gap" />
                <span className="orbital-platform right" /><span className="orbital-rover">▰</span>
              </div>
            ) : template.visual === 'race-art' ? (
              <div className="turbo-card-art" aria-label="Gün batımındaki yarış pistinde üç elektrikli GT aracı">
                <img src="/odevlendir/assets/turbo-duel/electric-gt-cars.png" alt="" />
                <span className="turbo-card-road" />
              </div>
            ) : template.visual === 'balance-art' ? (
              <div className="balance-card-art" aria-label="Gökyüzündeki denge platformunda bir oyuncu">
                <span className="balance-card-cloud left" /><span className="balance-card-cloud right" />
                <span className="balance-card-platform" /><span className="balance-card-player">●</span>
              </div>
            ) : template.visual === 'basket-art' ? (
              <div className="basket-card-art" aria-label="Gece oynanan LED ışıklı basketbol salonu">
                <span className="basket-card-board">POTADA<br />ÖĞREN</span>
                <span className="basket-card-ball" /><span className="basket-card-hoop" />
              </div>
            ) : template.visual === 'goal-art' ? (
              <div className="goal-card-art" aria-label="Projektörlerle aydınlatılmış futbol stadyumunda top, kaleci ve kale">
                <span className="goal-card-lights" /><span className="goal-card-net" />
                <span className="goal-card-keeper" /><span className="goal-card-ball" />
              </div>
            ) : (
              <div className="billionaire-card-art" aria-label="Altın ve mavi ışıklı televizyon bilgi yarışması sahnesi">
                <span className="billionaire-card-rings" /><span className="billionaire-card-logo">KİM<br /><b>MİLYARDER</b><br />OLMAK İSTER?</span>
                <span className="billionaire-card-answer left">A</span><span className="billionaire-card-answer right">B</span>
              </div>
            )}
            <div>
              <span className="draft-template">{template.badge}</span>
              <h2>{template.name}</h2>
              <p>{template.description}</p>
              <ul>{template.features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
              <button className="primary-button" type="button" onClick={() => onSelect(template.key)}>Bu şablonla oyun oluştur</button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function DemoGamePage({ onBack }: { onBack: () => void }) {
  return (
    <>
      <button className="ghost-button page-back" type="button" onClick={onBack}>← Ana sayfa</button>
      <section className="game-heading" aria-labelledby="game-title">
        <div><p className="eyebrow">Örnek etkinlik</p><h1 id="game-title">Tren Macerası</h1><p>Doğru balonu doğru anda vagona indir, öğrenme yolculuğunu tamamla.</p></div>
        <div className="game-badges" aria-label="Oyun bilgileri"><span>5 seviye</span><span>3 can</span><span>Süre sınırı yok</span></div>
      </section>
      <section className="play-card" aria-label="Tren Macerası oyun alanı">
        <PhaserGame />
        <p className="orientation-tip">Telefonda dikey oynayabilirsin; daha geniş bir görünüm için yatay çevirebilirsin.</p>
      </section>
      <section className="leaderboard" aria-labelledby="sample-leaders-title">
        <div><p className="eyebrow">Örnek tablo</p><h2 id="sample-leaders-title">En İyi Yolcular</h2></div>
        <div className="table-wrap"><table><thead><tr><th>Sıra</th><th>Oyuncu</th><th>Puan</th><th>Süre</th></tr></thead><tbody>
          {sampleLeaders.map((leader, index) => <tr key={leader.name}><td>{index + 1}</td><td>{leader.name}</td><td>{leader.score}</td><td>{leader.duration}</td></tr>)}
        </tbody></table></div>
        <p className="privacy-note">Bu tablo yalnızca örnek oyunun tanıtım verilerini gösterir.</p>
      </section>
    </>
  );
}
